/**
 * QC Engine API Client
 * Handles all API calls to /api/v2/qc and /api/v2/agents endpoints
 */

export interface QCJobRequest {
  asset_url: string;
  asset_name: string;
  media_type: "video" | "image";
  agent_slug: string;
  account_id: string;
  workspace_id: string;
  preview_url?: string;
  thumbnail_url?: string;
  instructions?: string;
  callback_url?: string;
  idempotency_key?: string;
  /**
   * Optional raw markdown QC rules pasted from the UI. When provided,
   * the backend uses this instead of fetching from Workbench / rules_url.
   * Capped at 64KB by the backend validator.
   */
  inline_rules?: string;
  /** Optional URL to a markdown QC rules file (fallback when inline_rules is not set). */
  rules_url?: string;
  metadata?: Record<string, any>;
}

export interface QCFindingAudit {
  raw_issue?: string;
  tool?: string | null;
  override_kind?: "deterministic" | "vlm_judge" | "overridden" | null;
  frame_indices?: number[];
  evidence_quote?: string | null;
  thresholds?: Record<string, number> | null;
  rule_id?: string | null;
  signals?: string[];
  confidence_band?: "high" | "medium" | "low" | null;
  policy_section?: string | null;
  code?: string | null;
  short_fix?: string | null;
}

export interface QCFinding {
  timestamp?: string;
  timestamp_seconds?: number;
  issue: string;
  severity: "critical" | "major" | "minor";
  policy_ref?: string;
  mention?: string;
  // Customer-facing presentation fields (additive; populated by the
  // backend humanizer). Older API responses won't have these — render
  // a fallback to ``issue`` when ``title`` is empty.
  title?: string;
  detail?: string;
  category?: string;
  recommended_action?: string | null;
  time_range?: string | null;
  frame_count?: number | null;
  audit?: QCFindingAudit;
  // Production-grade reference fields (additive). ``policy_section`` is
  // the human brand-guideline citation (e.g. "3.7 Headshot Depiction");
  // ``code`` is a stable engineering identifier suitable for ticketing;
  // ``short_fix`` is a one-line imperative for kanban cards.
  policy_section?: string | null;
  code?: string | null;
  short_fix?: string | null;
}

export interface QCJobResult {
  job_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  qc_status?: "passed" | "failed" | null;
  findings: QCFinding[];
  summary?: string;
  raw_analysis?: string;
  agent_slug: string;
  asset_name: string;
  media_type: string;
  processing_time_ms?: number;
  error?: string;
  created_at: string;
  completed_at?: string;
  metadata?: Record<string, any>;
  // Customer-facing roll-ups (additive). Empty for legacy jobs.
  display_summary?: string;
  severity_counts?: Record<string, number>;
}

export interface QCComment {
  id: string;
  author: {
    name: string;
    type: string;
    avatar_url?: string;
  };
  timecode?: string;
  timecode_seconds?: number;
  mention?: string;
  body: string;
  policy_ref?: string;
  severity: string;
  created_at: string;
}

export interface HealthCheckResponse {
  status: string;
  mongo: boolean;
  rag_db: boolean;
  workbench_mode?: string;
  version?: string;
}

export interface ApiRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: any;
  timestamp: number;
}

export interface ApiResponse {
  status: number;
  headers: Record<string, string>;
  body: any;
  timestamp: number;
  duration_ms: number;
}

export interface ApiCall {
  request: ApiRequest;
  response?: ApiResponse;
  error?: string;
}

const DEFAULT_QC_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const SENSITIVE_HEADERS = new Set([
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
]);

const redactHeaders = (headers: Record<string, string>): Record<string, string> => {
  const redacted: Record<string, string> = {};
  Object.entries(headers).forEach(([key, value]) => {
    redacted[key] = SENSITIVE_HEADERS.has(key.toLowerCase()) ? "[REDACTED]" : value;
  });
  return redacted;
};

export class QCEngineClient {
  private baseUrl: string;
  private token: string = "";
  private apiCalls: ApiCall[] = [];
  private onApiCall?: (call: ApiCall) => void;

  constructor(baseUrl: string = DEFAULT_QC_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string) {
    this.token = token;
  }

  setApiCallListener(listener?: (call: ApiCall) => void) {
    this.onApiCall = listener;
  }

  getApiCalls(): ApiCall[] {
    return this.apiCalls;
  }

  clearApiCalls() {
    this.apiCalls = [];
  }

  private logApiCall(apiCall: ApiCall) {
    this.apiCalls.push(apiCall);
    this.onApiCall?.(apiCall);
  }

  private async parseResponseBody(response: Response): Promise<any> {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return response.json();
    }

    const textBody = await response.text();
    if (!textBody) {
      return null;
    }

    try {
      return JSON.parse(textBody);
    } catch {
      return textBody;
    }
  }

  private async makeRequest(
    method: string,
    path: string,
    body?: any
  ): Promise<any> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const apiCall: ApiCall = {
      request: {
        method,
        url,
        headers: redactHeaders(headers),
        body,
        timestamp: Date.now(),
      },
    };

    const startTime = Date.now();

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const responseBody = await this.parseResponseBody(response);
      const duration = Date.now() - startTime;

      apiCall.response = {
        status: response.status,
        headers: redactHeaders(Object.fromEntries(response.headers.entries())),
        body: responseBody,
        timestamp: Date.now(),
        duration_ms: duration,
      };

      if (!response.ok) {
        const details =
          (typeof responseBody === "object" && responseBody && "detail" in responseBody
            ? String((responseBody as { detail: unknown }).detail)
            : typeof responseBody === "string"
              ? responseBody
              : response.statusText) || "Request failed";

        apiCall.error = `HTTP ${response.status}: ${details}`;
        this.logApiCall(apiCall);
        throw new Error(apiCall.error);
      }

      this.logApiCall(apiCall);
      return responseBody;
    } catch (error) {
      if (!apiCall.error) {
        apiCall.error = error instanceof Error ? error.message : String(error);
      }
      if (!this.apiCalls.includes(apiCall)) {
        this.logApiCall(apiCall);
      }
      throw error;
    }
  }

  async healthCheck(): Promise<HealthCheckResponse> {
    return this.makeRequest("GET", "/api/v2/qc/health");
  }

  async submitJob(request: QCJobRequest): Promise<QCJobResult> {
    return this.makeRequest("POST", "/api/v2/qc/jobs", request);
  }

  async getJob(jobId: string): Promise<QCJobResult> {
    return this.makeRequest("GET", `/api/v2/qc/jobs/${jobId}`);
  }

  async getComments(jobId: string): Promise<QCComment[]> {
    return this.makeRequest("GET", `/api/v2/qc/jobs/${jobId}/comments`);
  }

  async listJobs(params?: {
    account_id?: string;
    status?: string;
    agent_slug?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query.append(key, String(value));
        }
      });
    }
    const queryString = query.toString();
    return this.makeRequest("GET", `/api/v2/qc/jobs${queryString ? "?" + queryString : ""}`);
  }

  async listAgents(params: { account_id: string; workspace_id: string }): Promise<any> {
    const query = new URLSearchParams(params);
    return this.makeRequest("GET", `/api/v2/agents?${query.toString()}`);
  }

  async getAgentStats(params: {
    account_id: string;
    workspace_id: string;
    slug: string;
  }): Promise<any> {
    const { slug, ...queryParams } = params;
    const query = new URLSearchParams(queryParams);
    return this.makeRequest("GET", `/api/v2/agents/${params.slug}/stats?${query.toString()}`);
  }
}

export const qcClient = new QCEngineClient();
